import { UserRole, Environment } from "../types";
import { getAuthToken } from "./firebase";

export interface UserAccessSummary {
  role: UserRole;
  accessibleAgents: string[];
  accessiblePlugins: string[];
  canAccessDebugMenu: boolean;
}

export interface AgentAccess {
  agentType: string;
  canAccess: boolean;
  environment: string;
}

export interface PluginAccess {
  pluginType: string;
  canAccess: boolean;
  environment: string;
}

export interface DebugAccess {
  feature: string;
  canAccess: boolean;
  environment: string;
}

export interface RoleCheck {
  hasRole: boolean;
  requestedRole: UserRole;
}

export interface MinimumRoleCheck {
  hasMinimumRole: boolean;
  minimumRole: UserRole;
}

class RoleService {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 360 * 60 * 1000; // 360 minutes
  private readonly BASE_URL = process.env.REACT_APP_FUNCTIONS_BASE_URL;

  /**
   * Get user's complete access summary
   */
  async getUserAccessSummary(uid: string): Promise<UserAccessSummary> {
    const cacheKey = `user_access_${uid}`;

    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    try {
      const token = await getAuthToken();
      const response = await fetch(`${this.BASE_URL}/getUserAccess`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const accessSummary = result.data;

      this.updateCache(cacheKey, accessSummary);
      return accessSummary;
    } catch (error) {
      console.error("Error getting user access summary:", error);
      throw error;
    }
  }

  /**
   * Check if user can access debug features
   */
  async canAccessDebugFeature(uid: string, feature: string, environment?: Environment): Promise<boolean> {
    const cacheKey = `debug_access_${uid}_${feature}_${environment || "current"}`;

    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    try {
      const token = await getAuthToken();
      const response = await fetch(`${this.BASE_URL}/checkDebugAccess`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ feature, environment }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const canAccess = result.data.canAccess;

      this.updateCache(cacheKey, canAccess);
      return canAccess;
    } catch (error) {
      console.error("Error checking debug access:", error);
      return false;
    }
  }

  /**
   * Get user's role
   */
  async getUserRole(uid: string): Promise<UserRole> {
    const cacheKey = `user_role_${uid}`;

    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    try {
      const token = await getAuthToken();
      const response = await fetch(`${this.BASE_URL}/getUserRole`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const role = result.data.role;

      this.updateCache(cacheKey, role);
      return role;
    } catch (error) {
      console.error("Error getting user role:", error);
      return "user"; // Default role
    }
  }

  /**
   * Check if cache is still valid
   */
  private isCacheValid(key: string): boolean {
    const cached = this.cache.get(key);
    if (!cached) return false;

    return Date.now() - cached.timestamp < this.CACHE_TTL;
  }

  /**
   * Update cache with new data and timestamp
   */
  private updateCache(key: string, data: any): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Clear cache for a specific user
   */
  clearUserCache(uid: string): void {
    const keysToDelete: string[] = [];

    // Convert iterator to array to avoid TypeScript iteration issues
    const keys = Array.from(this.cache.keys());
    for (const key of keys) {
      if (key.includes(uid)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => this.cache.delete(key));
  }

  /**
   * Clear all cache
   */
  clearAllCache(): void {
    this.cache.clear();
  }

  /**
   * Set cache TTL (time to live)
   */
  setCacheTTL(ttl: number): void {
    // Use Object.defineProperty to update the readonly property
    Object.defineProperty(this, "CACHE_TTL", {
      value: ttl,
      writable: false,
      configurable: true,
    });
  }

  /**
   * Get current cache TTL
   */
  getCacheTTL(): number {
    return this.CACHE_TTL;
  }
}

// Export singleton instance
export const roleService = new RoleService();
